<template>
  <div
    class="bl-input-component"
    :class="{ 'bl-input-disabled': input.disabled }"
    :data-id="input.id"
  >
    <label
      v-if="input.label"
      :for="`bl-input-${id}`"
      class="bl-input-label"
      :class="input.error ? 'text-error-400' : ''"
      :data-id="input.id"
    >
      {{ input.label }}
      <span
        v-if="input.asterisk"
        class="absolute ml-1 text-xl text-error-400 -top-2"
        >*</span
      >
    </label>
    <span
      v-if="input.updating"
      class="absolute top-0 w-0 right-6 blum-loader-xs"
    ></span>
    <textarea
      v-if="input.textarea"
      type="text"
      class="bl-input bl-input-textarea"
      :class="inputClass"
      :id="`bl-input-${id}`"
      :data-id="input.id"
      :placeholder="input.placeholder"
      :readonly="input.readonly"
      :disabled="input.disabled"
      v-model="value"
      :required="input.asterisk"
    ></textarea>
    <input
      v-else
      :type="visible ? 'text' : input.inputType"
      class="bl-input"
      :class="[
        input.readonly ? 'text-grey-300' : '',
        input.disabled ? 'bg-grey-100 rounded' : '',
        dense ? 'bl-input-dense' : '',
        inputClass
      ]"
      :style="input.prefix ? { paddingLeft: input.prefixSpace } : {}"
      :id="`bl-input-${id}`"
      :min="input.min"
      :max="input.max"
      :data-id="input.id"
      :placeholder="input.placeholder"
      :readonly="input.readonly"
      :disabled="input.disabled"
      v-model="value"
      :required="input.asterisk"
      @focus="addWheelListener"
      @blur="removeWheelListener"
    />
    <div
      v-if="input.inputType === 'password'"
      class="absolute right-0 z-10"
      :class="input.label ? 'top-6' : ''"
    >
      <BlInputButton
        type="plain"
        :icon="visible ? 'eye-strike' : 'eye'"
        @clicked="visible = !visible"
      />
    </div>
    <div
      v-if="!borderless"
      class="bl-input-border"
      :class="[
        input.error ? 'border-error-400' : '',
        input.success ? 'border-success-400' : '',
        input.textarea ? 'bl-input-border-textarea' : '',
        dense ? 'bl-input-dense' : '',
        inputClass
      ]"
    >
      <BlTextBody2
        v-if="input.prefix"
        :text="input.prefix"
        class="bl-input-border-prefix"
      />
      <BlTextBody2
        v-if="input.suffix"
        :text="input.suffix"
        class="bl-input-border-suffix"
      />
    </div>
    <div v-if="input.error" class="text-xs text-error-400">
      {{ input.error === "true" ? "" : input.error }}
    </div>
    <div v-if="input.success" class="text-xs text-success-400">
      {{ input.success }}
    </div>
  </div>
</template>

<script>
import BlInputButton from "./BlInputButton.vue";
import BlTextBody2 from "./BlTextBody2.vue";
export default {
  name: "BlInputText",
  components: { BlInputButton, BlTextBody2 },
  props: {
    dense: { type: Boolean, default: false },
    borderless: { type: Boolean, default: false },
    inputClass: { type: String, default: "" },
    input: {
      label: { type: String, required: false },
      placeholder: { type: String, required: false },
      disabled: { type: Boolean, required: false },
      model: { type: String | Number, required: true },
      min: { type: String | Number, required: false },
      max: { type: String | Number, required: false },
      textarea: { type: Boolean, default: false },
      readonly: { type: Boolean, default: false },
      id: { type: String, required: false },
      prefix: { type: String, required: false },
      prefixSpace: { type: String, required: false },
      suffix: { type: String, required: false },
      inputType: { type: String, default: "text", required: false },
      error: { type: String, default: "", required: false },
      success: { type: String, default: "", required: false },
      asterisk: { type: Boolean, default: false },
      updating: { type: Boolean, default: false }
    }
  },
  data() {
    return {
      value: "",
      visible: false
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-input-${combination}`;
    }
  },
  watch: {
    value(val, oldVal) {
      if (this.input.inputType === "number" && val !== "") {
        if (/^0{2,}$/.test(val)) {
          this.value = "0";
          return;
        }

        if (/^0+[1-9]/.test(val)) {
          this.value = val.replace(/^0+/, "");
          return;
        }

        if (
          this.input.max !== undefined &&
          this.input.min !== undefined &&
          Number(this.input.max) < Number(this.input.min)
        ) {
          return;
        }
        const numVal = Number(val);
        if (!isNaN(numVal)) {
          if (this.input.max !== undefined && numVal > Number(this.input.max)) {
            this.value = this.input.max.toString();
            return;
          }
          if (this.input.min !== undefined && numVal < Number(this.input.min)) {
            this.value = this.input.min.toString();
            return;
          }
        }
      }

      if (val === oldVal) return;
      this.$emit("changed", { input: this.input, value: val });
    },
    input: {
      deep: true,
      immediate: true,
      handler(val) {
        this.value = val.model;
      }
    }
  },
  mounted() {
    setTimeout(() => {
      if (this.input.model) this.value = this.input.model;
    }, 1);
  },
  methods: {
    addWheelListener(e) {
      e.target.addEventListener("wheel", this.disableScroll);
    },
    removeWheelListener(e) {
      e.target.removeEventListener("wheel", this.disableScroll);
    },
    disableScroll(e) {
      e.preventDefault();
    }
  }
};
</script>

<style scoped lang="scss">
.bl-input-component {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  .bl-input-label {
    position: relative;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: calc(5 / 3);
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
    padding: 0;
    color: var(--Slate-10, #1d2939);
    & ~ .bl-input-border {
      top: 1.5rem;
    }
  }
  .bl-input {
    position: relative;
    width: 100%;
    height: 2.5rem;
    padding: 0 1rem;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: calc(11 / 7);
    letter-spacing: 0.5px;
    box-sizing: border-box;
    &.bl-input-textarea {
      height: 6.5rem;
      padding: 1rem;
      resize: none;
    }
    &:focus {
      outline: none;
      & + .bl-input-border {
        border-color: var(--primary-color);
      }
    }
  }
  .bl-input-border {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2.5rem;
    border-radius: 5px;
    box-sizing: border-box;
    border: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    .bl-input-border-prefix {
      position: absolute;
      padding-top: 0.125rem;
      left: 1rem;
      opacity: 0.5;
    }
    .bl-input-border-suffix {
      position: absolute;
      right: 1rem;
      opacity: 0.5;
    }
  }
  .bl-input-dense {
    height: 2rem;
    padding: 0 0.75rem;
  }
  .bl-input-border-textarea {
    height: 6.5rem;
  }
  &.bl-input-disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BlInputText.vue